// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
import { confirm } from '@automator/shared/utils/confirm';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import recipe builder services
import { getItem } from '@automator/builder/services/item';

// Import recipe builder utils
import { builderFetch } from '@automator/builder/utils';

// Import LitElement
import { html } from 'lit';

/**
 * Deletes an item from the recipe
 *
 * @param {String} itemType The item type (e.g. trigger)
 * @param {Number} itemId The item ID
 * @param {Object} atts The attributes. @todo Remove this once the new recipe object is implemented.
 * @return {Promise} The apiFetch promise
 */
const deleteItem = async ( itemType, itemId, atts = {} ) => {
	// Event name
	const eventName = 'uap/builder/core/item/delete-item';

	// Get the item
	const item = getItem( itemType, itemId );

	// If the item is not define, stop here
	if ( isEmpty( item ) ) {
		throw new Error( `deleteItem: The item ${ itemType } ${ itemId } doesn't exist` );
	}

	if (
		/**
		 * @todo Remove this once the new recipe object is implemented and all the validations are moved to this file.
		 */
		! atts.__deprecated__ignoreValidation &&

		// Check if the recipe is live
		UncannyAutomator._recipe.is_recipe_on &&
		// Check if the item itself is also live
		item.is_item_on
	) {
		await confirm( {
			content: html`
				<uo-alert type="warning">
					${ escapeHTML( __( 'Deleting items in a live recipe can lead to unexpected behaviors.', 'uncanny-automator' ) ) }
				</uo-alert>
			`
		} );
	}

	// Save the recipe title
	return builderFetch( {
		eventName: eventName,
		endpoint: 'delete',
		method: 'POST',
		data: {
			// eslint-disable-next-line camelcase
			recipe_id: UncannyAutomator._recipe.recipe_id,
			ID: itemId,
		},
		snackbar: {
			loading: itemType === 'trigger'
				? escapeHTML( __( 'Deleting trigger', 'uncanny-automator' ) )
				: escapeHTML( __( 'Deleting action', 'uncanny-automator' ) ),
			success: itemType === 'trigger'
				? escapeHTML( __( 'Trigger deleted successfully', 'uncanny-automator' ) )
				: escapeHTML( __( 'Action deleted successfully', 'uncanny-automator' ) ),
		}
	} ).then( response => {
		// Check if the call was successful
		if ( response.success ) {
			// Send custom event to the document
			document.dispatchEvent( new CustomEvent( eventName, {
				detail: {
					itemID: itemId
				}
			} ) );
		}

		return response;
	} );
}

export default deleteItem;
